import React, { useState } from 'react';
import { useLeadStore } from '@/store/useLeadStore';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Loader } from '../UI/Loader';
import { useLeadSubmit } from '@/hooks/useLeadSubmit';
import { useWidgetConfig } from '@/hooks/useWidgetConfig';
import { validateData } from '@/utils/validation';
import { createStep4Schema } from '@/config/validation.schemas';

export const Step5Contact: React.FC = () => {
  const { formData, goToStep, setUser } = useLeadStore();
  const config = useWidgetConfig();
  const { submit, loading, error } = useLeadSubmit(config);
  
  const [formValues, setFormValues] = useState({
    lastName: formData.user?.lastName || '',
    firstName: formData.user?.firstName || '',
    company: formData.user?.company || '',
    email: formData.user?.email || '',
    phone: formData.user?.phone || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = { ...formValues, [field]: e.target.value };
    setFormValues(newValues);
    
    // Save to store immediately
    setUser({
      lastName: newValues.lastName,
      firstName: newValues.firstName,
      company: newValues.company,
      email: newValues.email,
      phone: newValues.phone,
    });
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    const schema = createStep4Schema(config.withPhone);
    const validation = validateData(schema, formValues);

    if (!validation.success) {
      setErrors(validation.errors || {});
      return;
    }

    // Update the store with user data BEFORE submission
    const { setUser } = useLeadStore.getState();
    setUser({
      lastName: formValues.lastName,
      firstName: formValues.firstName,
      company: formValues.company,
      email: formValues.email,
      phone: formValues.phone,
    });

    // Submit with the fresh user data
    await submit();
  };

  return (
    <div className="animate-fade-in">
            <h2 className="text-[28px] font-light tracking-tight mb-2">Recevez votre estimation</h2>
      <p className="text-sm text-secondary mb-10">
        Renseignez vos coordonnées
      </p>

      <Input
        label="Nom *"
        type="text"
        value={formValues.lastName}
        onChange={handleChange('lastName')}
        placeholder="Votre nom"
        error={errors.lastName}
      />

      <Input
        label="Prénom *"
        type="text"
        value={formValues.firstName}
        onChange={handleChange('firstName')}
        placeholder="Votre prénom"
        error={errors.firstName}
      />

      <Input
        label="Entreprise"
        type="text"
        value={formValues.company}
        onChange={handleChange('company')}
        placeholder="Optionnel"
      />

      <Input
        label="Email *"
        type="email"
        value={formValues.email}
        onChange={handleChange('email')}
        placeholder="exemple@email.com"
        error={errors.email}
      />

      {config.withPhone && (
        <Input
          label="Téléphone *"
          type="tel"
          value={formValues.phone}
          onChange={handleChange('phone')}
          placeholder="06 12 34 56 78"
          error={errors.phone}
        />
      )}

      {error && (
        <div className="text-[#D32F2F] text-sm mb-4">{error}</div>
      )}

      <div className="flex gap-3 mt-10">
        <Button variant="secondary" onClick={() => goToStep(4)}>
          Retour
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <Loader /> : 'Recevoir mon estimation'}
        </Button>
      </div>
    </div>
  );
};
